'use client';

import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {useId, type ReactNode} from 'react';
import {useScrollFade} from '../../hooks/useScrollFade';

import {CloseButton} from './CloseButton';
import {ResponsiveModal} from './ResponsiveModal';
import {Typography, type FontSize} from './Typography';

export interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleHidden?: boolean;
  titleMobileSize?: FontSize;
  maxWidth?: number;
  // Drops the inner white card and turns the shell itself white, so children
  // sit directly on a single surface — the Register Interest confirmation
  // state per Figma 214-24441. Pass per-state (e.g. `plain={submitted}`), not
  // for a whole modal, or a state that still needs the inner card loses it too.
  plain?: boolean;
  // Fade the bottom edge of the scrolling body when its content overflows.
  fadeOverflow?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

// `minHeight: 0` lets the shell shrink inside the dialog/drawer paper so the
// Body — not the paper — is what overflows, which keeps the Header on screen.
const Shell = styled(Box, {shouldForwardProp: prop => prop !== 'plain'})<{plain?: boolean}>(({theme, plain}) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  backgroundColor: plain ? theme.palette.colors.surface.neutral.default : theme.palette.colors.surface.neutral.subtle,
}));

const Header = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  paddingBlockEnd: theme.spacing(3),
  // Never let the flex column give this back to the scrolling Body below it —
  // the title and close icon must stay fixed while only the Body scrolls.
  flexShrink: 0,
}));

// GROV-267: long lists trail off at the bottom edge instead of being cut
// square, so it reads as "there is more below". Driven by useScrollFade, which
// only reports true while the body scrolls and has not reached the end — a list
// that already fits, or one scrolled to the bottom, is never dimmed.
// Both drawers in Figma carry a 104px "Gradient shadow" anchored to the bottom
// of the list (amenities node 24-7377, nearby node 24-7401).
const OVERFLOW_FADE_HEIGHT = '104px';

// [MASTER] Drawer: "the Content area is capped at 604px and scrolls" while the
// header and CTA section stay pinned. The paper's own dvh bound stays as a
// guard for short viewports.
const DRAWER_CONTENT_MAX_HEIGHT = 604;

const MODAL_CONTENT_MAX_HEIGHT = 498;

const Body = styled(Box, {
  shouldForwardProp: propName => propName !== '$fadeOverflow' && propName !== '$capped' && propName !== '$footer',
})<{
  $fadeOverflow?: boolean;
  $capped?: boolean;
  $footer?: boolean;
}>(({theme, $fadeOverflow, $capped, $footer}) => ({
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing(4),
  paddingBlockStart: 0,
  ...($footer && {paddingBlockEnd: 0}),
  [theme.breakpoints.down('md')]: {maxHeight: DRAWER_CONTENT_MAX_HEIGHT},
  ...($capped && {[theme.breakpoints.up('md')]: {maxHeight: MODAL_CONTENT_MAX_HEIGHT}}),
  ...($fadeOverflow && {
    maskImage: `linear-gradient(to bottom, black calc(100% - ${OVERFLOW_FADE_HEIGHT}), transparent 100%)`,
  }),
}));

// [MASTER] Drawer "CTA section" / [MASTER] Modal "Footer": pinned under the
// scrolling Body, never inside it.
const Footer = styled(Box)(({theme}) => ({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3, 4, 5),
  [theme.breakpoints.up('md')]: {paddingBlockEnd: theme.spacing(6.25)},
}));

// Stays in the tree (only its own styling toggles) rather than being swapped
// for a bare `children` render: a conditional wrapper at this position would
// change the element type React sees here, which unmounts and remounts
// `children` — dropping the confirmation state right as it switches in.
const Content = styled(Box, {shouldForwardProp: prop => prop !== 'plain'})<{plain?: boolean}>(({theme, plain}) =>
  plain
    ? {}
    : {
        backgroundColor: theme.palette.colors.surface.neutral.default,
        borderRadius: theme.radii.md,
        padding: theme.spacing(3),
      },
);

export function BaseModal({
  open,
  onClose,
  title,
  titleHidden = false,
  titleMobileSize = 'title-6',
  maxWidth,
  plain = false,
  fadeOverflow = false,
  footer,
  children,
}: BaseModalProps) {
  const titleId = useId();
  const {ref: scrollRef, faded} = useScrollFade(fadeOverflow);

  return (
    <ResponsiveModal open={open} onClose={onClose} labelledBy={titleId} maxWidth={maxWidth} radius="card">
      <Shell plain={plain}>
        <Header>
          <Typography
            as="h2"
            size="title-4"
            weight="semiBold"
            mobileSize={titleMobileSize}
            id={titleId}
            sx={{visibility: titleHidden ? 'hidden' : 'visible'}}
          >
            {title}
          </Typography>
          <CloseButton onClick={onClose} />
        </Header>
        <Body ref={scrollRef} $fadeOverflow={faded} $capped={fadeOverflow} $footer={!!footer}>
          <Content plain={plain}>{children}</Content>
        </Body>
        {!!footer && <Footer>{footer}</Footer>}
      </Shell>
    </ResponsiveModal>
  );
}

BaseModal.displayName = 'BaseModal';
